/**
 * Pure date-window helpers for the date-range dashboards (#1073, WS-C).
 *
 * A "window" is an inclusive `[from, to]` pair of `YYYY-MM-DD` strings.
 * The default dashboard window is the current calendar quarter (founder-
 * confirmed). The quarter math mirrors the backend `current_quarter()`
 * in `api/routes/_windowed_stats.py`, so the SPA and API agree on the
 * exact bounds.
 *
 * Pure (no React, no I/O) so the quarter arithmetic is unit-tested in
 * isolation (`web/tests/dateWindow.test.ts`). ISO date strings sort
 * chronologically, so range comparisons are plain string compares.
 */

export interface DateWindow {
  /** Inclusive start, `YYYY-MM-DD`. */
  from: string;
  /** Inclusive end, `YYYY-MM-DD`. */
  to: string;
}

/** Parse a `YYYY-MM-DD` string as a local calendar date (no TZ shift). */
export function parseISO(iso: string): Date {
  const [y = 1970, m = 1, d = 1] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Short "Mon D" label for a `YYYY-MM-DD` string. */
export function shortLabel(iso: string): string {
  return parseISO(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Weekday-anchored "Mon 6 Jul" label for a `YYYY-MM-DD` string (#1280).
 * The timeline drag preview needs the endpoints' day-of-week, not just
 * month+day. Built from `parseISO` (local calendar date, no TZ shift —
 * same guarantee as the rest of this file) and pinned to `en-GB`
 * day-before-month order so the label reads identically for every
 * viewer (mirrors `formatLongDate` in `components/roster/rosterGrid.ts`).
 */
export function weekdayShortLabel(iso: string): string {
  const d = parseISO(iso);
  const weekday = d.toLocaleDateString("en-GB", { weekday: "short" });
  const month = d.toLocaleDateString("en-GB", { month: "short" });
  return `${weekday} ${d.getDate()} ${month}`;
}

/** The calendar quarter index (0–3) a 0-based `month` belongs to. */
function quarterIndex(month: number): number {
  return Math.floor(month / 3);
}

/** Inclusive bounds of the calendar quarter containing `today`. */
export function currentQuarter(today: Date = new Date()): DateWindow {
  const y = today.getFullYear();
  const startMonth = quarterIndex(today.getMonth()) * 3;
  return {
    from: toISO(new Date(y, startMonth, 1)),
    // Day 0 of the next quarter's first month = the quarter's last day.
    to: toISO(new Date(y, startMonth + 3, 0)),
  };
}

/** The quarter `dir` steps from the one containing `window.from`
 * (`-1` previous, `+1` next). Crosses year boundaries via `Date`
 * month-overflow normalisation. */
export function shiftQuarter(window: DateWindow, dir: -1 | 1): DateWindow {
  const anchor = parseISO(window.from);
  const startMonth = quarterIndex(anchor.getMonth()) * 3 + dir * 3;
  return currentQuarter(new Date(anchor.getFullYear(), startMonth, 1));
}

/** "Q2 2026" for the quarter containing `window.from`. */
export function quarterLabel(window: DateWindow): string {
  const d = parseISO(window.from);
  return `Q${quarterIndex(d.getMonth()) + 1} ${d.getFullYear()}`;
}

/** Whether `window` is exactly one calendar quarter's bounds (so the
 * selector shows the quarter label rather than a custom range). */
export function isWholeQuarter(window: DateWindow): boolean {
  const q = currentQuarter(parseISO(window.from));
  return q.from === window.from && q.to === window.to;
}

/** Whether two windows have identical bounds. */
export function sameWindow(a: DateWindow, b: DateWindow): boolean {
  return a.from === b.from && a.to === b.to;
}

/** Inclusive, chronological validity (`from` on or before `to`). */
export function isValidRange(from: string, to: string): boolean {
  return from <= to;
}

/** `iso + n` days as a fresh `YYYY-MM-DD` string. Local-time arithmetic
 * — `parseISO`/`toISO` round-trip avoids the UTC-midnight TZ drift that
 * `new Date(iso)` would introduce on positive UTC offsets. */
export function addDays(iso: string, n: number): string {
  const d = parseISO(iso);
  d.setDate(d.getDate() + n);
  return toISO(d);
}

/** Whole calendar days from `from` to `to` (signed, `to - from`). Days
 * are 86_400_000 ms apart in local time too, so a millisecond diff /
 * day-ms is exact for two midnight-local dates produced by `parseISO`. */
export function daysBetween(from: string, to: string): number {
  return Math.round((parseISO(to).getTime() - parseISO(from).getTime()) / 86_400_000);
}

// ─── schedule-timeline preset windows (Task 3 / founder 2026-06-29) ─────────
//
// The schedule defaults to a window TRUE-DAY-CENTERED on today (3 months),
// with a 6-month centered option and a "whole year" option that is NOT
// centered (Jan 1–Dec 31 of the current calendar year). The 3/6-month
// windows are EXACTLY symmetric around today — equal day half-spans before
// and after — so look-back always equals look-ahead regardless of the
// day-of-month (the earlier month-snapped bounds leaned strongly past near
// month-end; #1233 fix). `Date` day-arithmetic via `addDays` normalises
// across month and year boundaries.

/** The schedule-timeline window presets, in display order. */
export type WindowPreset = "3m" | "6m" | "year";

/** Symmetric half-span (in days) on EACH side of today for each preset.
 * Clean, fixed values so the window is exactly day-centered: the total span
 * is `2 * half + 1` days (today + an equal number of days before/after).
 * `45` → 91-day "3 months"; `91` → 183-day "6 months". */
const CENTERED_HALF_SPAN: Record<3 | 6, number> = { 3: 45, 6: 91 };

/** A window EXACTLY symmetric around `today`: `today − half` to
 * `today + half` days, where `half` is the preset's fixed half-span. Today
 * is the precise centre, with equal look-back and look-ahead (no
 * day-of-month skew). 3-month (half 45) → `today ± 45 days`; 6-month
 * (half 91) → `today ± 91 days`. `addDays` normalises across month / year
 * boundaries. */
export function centeredWindow(months: 3 | 6, today: Date = new Date()): DateWindow {
  const half = CENTERED_HALF_SPAN[months];
  const todayISO = toISO(today);
  return { from: addDays(todayISO, -half), to: addDays(todayISO, half) };
}

/** Jan 1 – Dec 31 of the calendar year containing `today` (NOT centered). */
export function currentYearWindow(today: Date = new Date()): DateWindow {
  const y = today.getFullYear();
  return { from: toISO(new Date(y, 0, 1)), to: toISO(new Date(y, 11, 31)) };
}

/** The `{from,to}` bounds for a named preset, relative to `today`. */
export function presetWindow(preset: WindowPreset, today: Date = new Date()): DateWindow {
  switch (preset) {
    case "3m":
      return centeredWindow(3, today);
    case "6m":
      return centeredWindow(6, today);
    case "year":
      return currentYearWindow(today);
  }
}

/** Which preset (if any) a window's bounds exactly match, relative to
 * `today` — drives the active chip in the selector. `null` for a custom
 * range that matches no preset. */
export function matchedPreset(window: DateWindow, today: Date = new Date()): WindowPreset | null {
  const presets: WindowPreset[] = ["3m", "6m", "year"];
  for (const p of presets) {
    if (sameWindow(window, presetWindow(p, today))) return p;
  }
  return null;
}

/** Short human label for a preset chip / caption. */
export const PRESET_LABEL: Record<WindowPreset, string> = {
  "3m": "3 months",
  "6m": "6 months",
  year: "Whole year",
};
